import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Navigation } from 'lucide-react';
import { useJsApiLoader } from '@react-google-maps/api';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import { getLocationSuggestions, reverseGeocode } from '@/services/api';
import { BRAND } from '@/config/brand';
import { cn } from '@/lib/utils';

const GMAPS_LIBRARIES = ['marker', 'places'];

const FL_BOUNDS = { west: -87.6, south: 24.4, east: -80.0, north: 31.1 };
let _sessionToken = null;

function getSessionToken() {
  if (!_sessionToken) {
    try { _sessionToken = new window.google.maps.places.AutocompleteSessionToken(); } catch (_) {}
  }
  return _sessionToken;
}

function renewSessionToken() {
  _sessionToken = null;
  try { _sessionToken = new window.google.maps.places.AutocompleteSessionToken(); } catch (_) {}
}

async function fetchGoogleSuggestions(q) {
  if (!window.google?.maps?.places?.AutocompleteSuggestion) return null;
  try {
    const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: q,
      sessionToken: getSessionToken(),
      includedRegionCodes: ['us'],
      locationRestriction: FL_BOUNDS,
    });
    if (!suggestions) return [];
    return suggestions
      .filter((s) => s.placePrediction)
      .slice(0, 6)
      .map((s) => {
        const p = s.placePrediction;
        return {
          placeId: p.placeId,
          description: p.text?.text || '',
          mainText: p.structuredFormat?.mainText?.text || '',
          secondaryText: p.structuredFormat?.secondaryText?.text || '',
          _prediction: p,
        };
      });
  } catch (_) {
    return null;
  }
}

async function resolvePlaceDetails(prediction) {
  try {
    const place = prediction._prediction.toPlace();
    await place.fetchFields({
      fields: ['location', 'viewport', 'addressComponents', 'formattedAddress', 'displayName', 'types'],
    });
    renewSessionToken();
    return place;
  } catch (_) {
    renewSessionToken();
    return null;
  }
}

function findComp(comps, type) {
  return comps?.find((c) => (c.types || []).includes(type));
}

function normalizeCity(name) {
  if (!name) return '';
  return String(name).replace(/,\s*(FL|Florida)\s*$/i, '').trim();
}

function normalizeZip(z) {
  if (!z) return '';
  const m = String(z).trim().match(/^(\d{5})/);
  return m ? m[1] : String(z).trim();
}

function looksLikeZip(s) {
  return /^\d{5}(-\d{4})?$/.test(String(s || '').trim());
}

function parseLocFromText(text) {
  const raw = String(text || '').trim();
  if (!raw) return {};
  if (looksLikeZip(raw)) return { zip: normalizeZip(raw) };
  const zipIn = raw.match(/\b(\d{5})(?:-\d{4})?\b/);
  if (zipIn && /^\d{5}/.test(raw)) return { zip: zipIn[1] };
  const city = normalizeCity(raw.split(',')[0] || raw);
  if (city && !looksLikeZip(city)) return { city };
  return {};
}

function mlsToSuggestion(r) {
  const zipKey = normalizeZip(r.zip || (r.type === 'zip' ? (r.value || r.label || '') : ''));
  const cityKey = normalizeCity(r.city || r.name || (r.type === 'city' ? (r.value || r.label || '') : ''));
  const label = r.label || r.name || cityKey || zipKey || '';
  return {
    placeId: null,
    description: label,
    mainText: r.type === 'zip' || zipKey ? (zipKey || label) : (cityKey || label),
    secondaryText: (r.type === 'zip' || zipKey) ? 'ZIP' : (r.state ? `${r.state} · City` : 'City'),
    _fallback: {
      ...r,
      zip: zipKey || r.zip || '',
      city: cityKey || r.city || '',
      type: (r.type === 'zip' || zipKey) ? 'zip' : (r.type || 'city'),
    },
  };
}

function buildPayload(place, description, hint = {}) {
  const comps = place.addressComponents || [];
  const streetNum = findComp(comps, 'street_number');
  const route = findComp(comps, 'route');
  const zip = findComp(comps, 'postal_code');
  const city = findComp(comps, 'locality') || findComp(comps, 'postal_town');
  const neighborhood = comps.find((c) => {
    const t = c.types || [];
    return t.includes('neighborhood') || t.includes('sublocality') || t.includes('sublocality_level_1');
  });

  let viewport = null;
  let location = null;
  try { viewport = place.viewport; } catch (_) {}
  try {
    if (place.location) location = { lat: place.location.lat(), lng: place.location.lng() };
  } catch (_) {}

  const zipVal = normalizeZip(zip?.longText || zip?.shortText || zip?.long_name || zip?.short_name || '');
  const cityVal = normalizeCity(city?.longText || city?.shortText || city?.long_name || city?.short_name || '');
  const types = place.types || [];
  const isPostal = types.includes('postal_code') || looksLikeZip(description) || looksLikeZip(hint.mainText);

  let patch;
  if (route && !streetNum) {
    patch = { streetName: route.longText || route.shortText || route.long_name || route.short_name || '' };
  } else if (streetNum && route) {
    return { isPreciseAddress: true, viewport, location };
  } else if (zipVal && (isPostal || !cityVal)) {
    patch = { zip: zipVal };
  } else if (neighborhood) {
    patch = { neighborhood: normalizeCity(neighborhood.longText || neighborhood.shortText || neighborhood.long_name || neighborhood.short_name || '') };
    if (cityVal) patch.city = cityVal;
  } else if (cityVal) {
    patch = { city: cityVal };
  } else if (zipVal) {
    patch = { zip: zipVal };
  } else {
    patch = parseLocFromText(hint.mainText || description);
    if (!patch.city && !patch.zip) patch = { q: description };
  }

  // Never send city/zip search as only `q` when text looks like FL city/ZIP
  if (!patch.zip && !patch.city && !patch.neighborhood && !patch.streetName) {
    const inferred = parseLocFromText(hint.mainText || description);
    if (inferred.zip || inferred.city) patch = { ...patch, ...inferred };
  }

  return { ...patch, viewport, location };
}

export function PlacesSearchBar({
  onSelect,
  onCurrentLocation,
  onInputChange,
  className,
  size = 'default',
  inputClassName,
}) {
  const { isLoaded } = useJsApiLoader({
    id: 'casahom-gmaps',
    googleMapsApiKey: BRAND.googleMapsApiKey,
    libraries: GMAPS_LIBRARIES,
  });

  const isHero = size === 'hero';
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const inputRef = useRef(null);
  const debouncedInput = useDebounce(input, 200);

  useEffect(() => {
    if (!debouncedInput || debouncedInput.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;

    (async () => {
      const [googleResults, mlsRaw] = await Promise.all([
        fetchGoogleSuggestions(debouncedInput),
        getLocationSuggestions(debouncedInput).catch(() => []),
      ]);
      if (cancelled) return;

      const mlsItems = (mlsRaw || [])
        .filter((r) => r.type === 'city' || r.type === 'zip' || r.zip || r.city)
        .slice(0, 5)
        .map(mlsToSuggestion);

      const googleItems = googleResults === null ? [] : googleResults;
      const seen = new Set();
      const merged = [];
      for (const item of [...mlsItems, ...googleItems]) {
        const key = (item.mainText || item.description || '').toLowerCase().replace(/,\s*(fl|florida)\s*$/i, '').trim();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push(item);
      }
      setSuggestions(merged.slice(0, 8));
      setOpen(merged.length > 0);
    })();

    return () => { cancelled = true; };
  }, [debouncedInput, isLoaded]);

  const handleCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          let city = '';
          let zip = '';
          if (window.google?.maps?.Geocoder) {
            const geocoder = new window.google.maps.Geocoder();
            const results = await new Promise((resolve) => {
              geocoder.geocode({ location: { lat, lng } }, (res, status) => resolve(status === 'OK' ? res : null));
            });
            if (results?.[0]) {
              const comps = results[0].address_components || [];
              const find = (type) => comps.find((c) => (c.types || []).includes(type));
              const cityComp = find('locality') || find('postal_town') || find('administrative_area_level_2') || find('sublocality');
              const zipComp = find('postal_code');
              city = cityComp?.long_name || '';
              zip = zipComp?.long_name || '';
            }
          }
          if (!city) {
            const place = await reverseGeocode(lat, lng);
            city = place?.city || '';
            zip = zip || place?.zip || '';
          }
          setInput(city || zip || '');
          setSuggestions([]);
          setOpen(false);
          const payload = city
            ? { city, viewport: null, location: { lat, lng } }
            : { zip, viewport: null, location: { lat, lng } };
          if (onCurrentLocation) onCurrentLocation(payload);
          else onSelect?.(payload);
        } catch (_) {
          const payload = { viewport: null, location: { lat, lng } };
          if (onCurrentLocation) onCurrentLocation(payload);
          else onSelect?.(payload);
        } finally {
          setLocating(false);
        }
      },
      () => {
        setLocating(false);
        toast.error('Could not get your location. Please allow location access.');
      },
      { maximumAge: 300000, timeout: 8000 },
    );
  }, [onSelect, onCurrentLocation]);

  const handleSelect = useCallback(
    async (s) => {
      setInput(s.description || s.mainText || '');
      setOpen(false);
      setSuggestions([]);

      if (s._fallback) {
        const r = s._fallback;
        const patch = {};
        if (r.type === 'zip' || looksLikeZip(r.zip) || looksLikeZip(r.value) || looksLikeZip(s.mainText)) {
          patch.zip = normalizeZip(r.zip || r.value || s.mainText || '');
        } else if (r.type === 'city' || r.city || r.name || r.label) {
          patch.city = normalizeCity(r.city || r.name || r.label || s.mainText || '');
        } else if (r.type === 'neighborhood') {
          patch.neighborhood = r.neighborhood || r.value || r.name || '';
          if (r.city) patch.city = normalizeCity(r.city);
        } else {
          Object.assign(patch, parseLocFromText(s.mainText || r.label || r.value || ''));
          if (!patch.city && !patch.zip) patch.q = r.label || r.value || '';
        }
        onSelect?.({ ...patch, viewport: null, location: null });
        return;
      }

      const place = await resolvePlaceDetails(s);
      if (!place) {
        const inferred = parseLocFromText(s.mainText || s.description);
        if (inferred.city || inferred.zip) onSelect?.({ ...inferred, viewport: null, location: null });
        return;
      }
      onSelect?.(buildPayload(place, s.description, { mainText: s.mainText }));
    },
    [onSelect]
  );

  return (
    <div className={cn('relative', className)}>
      <div
        className={cn(
          'flex items-center gap-2 border border-slate-200 bg-white transition-shadow focus-within:border-transparent focus-within:ring-2 focus-within:ring-blue-500',
          isHero
            ? 'h-14 rounded-xl px-4 shadow-none focus-within:ring-4 focus-within:ring-blue-200'
            : 'rounded-full px-3 py-1.5 shadow-sm',
        )}
      >
        <Search
          size={isHero ? 20 : 15}
          className="shrink-0 text-slate-400"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => { setInput(e.target.value); onInputChange?.(e.target.value); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Address, neighborhood, city, ZIP"
          className={cn(
            'min-w-0 flex-1 bg-transparent text-slate-800 placeholder-slate-400 outline-none',
            isHero ? 'text-base' : 'text-sm',
            inputClassName,
          )}
          aria-label="Search location"
          aria-autocomplete="list"
          aria-expanded={open}
          autoComplete="off"
        />
        {input && (
          <button
            onClick={() => { setInput(''); setSuggestions([]); setOpen(false); onInputChange?.(''); inputRef.current?.focus(); }}
            className={cn(
              'shrink-0 text-slate-400 hover:text-slate-600',
              isHero && 'rounded-lg p-1 hover:bg-slate-100',
            )}
            aria-label="Clear search"
            type="button"
          >
            <X size={isHero ? 18 : 14} />
          </button>
        )}
      </div>

      {open && (
        <ul
          className={cn(
            'absolute left-0 right-0 top-full z-50 mt-1.5 max-h-72 overflow-auto rounded-xl border border-slate-200 bg-white shadow-xl',
            isHero && 'mt-2',
          )}
          role="listbox"
        >
          <li
            role="option"
            aria-selected={false}
            onMouseDown={handleCurrentLocation}
            className={cn(
              'flex cursor-pointer items-center gap-2 border-b border-slate-100 text-blue-600 hover:bg-slate-50',
              isHero ? 'px-4 py-3.5' : 'px-4 py-2.5',
            )}
          >
            <Navigation size={isHero ? 17 : 15} className="shrink-0" />
            <span className={cn('font-medium', isHero ? 'text-base' : 'text-sm')}>
              {locating ? 'Locating…' : 'Current location'}
            </span>
          </li>
          {suggestions.map((s, i) => (
            <li
              key={s.placeId || i}
              role="option"
              aria-selected={false}
              onMouseDown={() => handleSelect(s)}
              className={cn(
                'flex cursor-pointer flex-col border-b border-slate-100 last:border-0 hover:bg-slate-50',
                isHero ? 'px-4 py-3' : 'px-4 py-2.5',
              )}
            >
              <span className={cn('truncate font-medium text-slate-900', isHero ? 'text-base' : 'text-sm')}>
                {s.mainText || s.description}
              </span>
              {s.secondaryText && (
                <span className={cn('truncate text-slate-500', isHero ? 'text-sm' : 'text-xs')}>
                  {s.secondaryText}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
